import React, { useState, useRef, useEffect } from "react";
import { API_BASE_URL } from "../api/config";

type PanelSize = "default" | "maximized";

interface Message {
  role: "user" | "assistant";
  content: string;
}

async function sendMessage(
  message: string,
  conversationId: string | null
): Promise<{ reply: string; conversationId: string }> {
  const res = await fetch(`${API_BASE_URL}/api/chat/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, conversationId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error ?? "Request failed");
  }
  return res.json();
}

const CopilotChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<PanelSize>("default");
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleOpen = () => { setOpen(true); setSize("default"); };
  const handleClose = () => { setOpen(false); setSize("default"); };

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { role: "user", content: text };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setInput("");
    setError(null);
    setLoading(true);

    try {
      const result = await sendMessage(text, conversationId);
      setConversationId(result.conversationId);
      setMessages([...newHistory, { role: "assistant", content: result.reply }]);
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const panelClass = [
    "copilot-panel",
    open ? "copilot-panel--open" : "",
    open && size === "maximized" ? "copilot-panel--maximized" : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button className="copilot-fab" onClick={handleOpen} aria-label="AI Chat">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="#ffffff" fillOpacity="0.95" />
            <circle cx="12.5" cy="3.5" r="1" fill="#ffffff" fillOpacity="0.6" />
            <circle cx="3.5" cy="11.5" r="0.75" fill="#ffffff" fillOpacity="0.5" />
          </svg>
          Financial Insights AI
        </button>
      )}

      {/* Slide-in chat panel */}
      <div className={panelClass}>
        <div className="copilot-panel-header">
          <div className="copilot-panel-title">
            <div className="copilot-panel-icon">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="white" />
              </svg>
            </div>
            <div>
              <div className="copilot-panel-title-text">Financial Insights AI</div>
              <div className="copilot-panel-subtitle">
                <span className="copilot-status-dot" />
                Online
              </div>
            </div>
          </div>
          <div className="copilot-panel-actions">
            <button
              className="copilot-panel-action-btn"
              onClick={() => setSize(size === "maximized" ? "default" : "maximized")}
              aria-label={size === "maximized" ? "Restore" : "Maximise"}
              title={size === "maximized" ? "Restore" : "Maximise"}
            >
              {size === "maximized" ? (
                <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                  <rect x="1" y="3" width="8" height="8" rx="1" stroke="white" strokeWidth="1.5"/>
                  <path d="M4 3V2h6v6h-1" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                  <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" stroke="white" strokeWidth="1.5"/>
                </svg>
              )}
            </button>
            <button
              className="copilot-panel-action-btn"
              onClick={() => { setMessages([]); setConversationId(null); setError(null); }}
              aria-label="Clear chat"
              title="Clear chat"
            >
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                <path d="M2 3h8M5 3V2h2v1M4.5 3l.5 6h2l.5-6" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="copilot-panel-action-btn copilot-panel-close-btn" onClick={handleClose} aria-label="Close chat" title="Close">
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                <path d="M2 2l8 8M10 2L2 10" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="copilot-panel-body">
          {/* Messages */}
          <div className="copilot-messages">
            {messages.length === 0 && !loading && (
              <div className="copilot-empty-state">
                <div className="copilot-empty-icon">
                  <svg width="28" height="28" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="#2590F4" fillOpacity="0.7" />
                  </svg>
                </div>
                <div className="copilot-empty-title">VarianceIQ</div>
                <div className="copilot-empty-subtitle">Ask me about budget variances, GL accounts, cost centers, or any financial insights.</div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`copilot-msg copilot-msg--${msg.role}`}>
                {msg.role === "assistant" && (
                  <div className="copilot-msg-avatar">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="white" />
                    </svg>
                  </div>
                )}
                <div className="copilot-msg-bubble">{msg.content}</div>
              </div>
            ))}

            {loading && (
              <div className="copilot-msg copilot-msg--assistant">
                <div className="copilot-msg-avatar">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1.5L9.2 5.8L13.5 7L9.2 8.2L8 12.5L6.8 8.2L2.5 7L6.8 5.8L8 1.5Z" fill="white" />
                  </svg>
                </div>
                <div className="copilot-msg-bubble copilot-msg-typing">
                  <span className="copilot-typing-label">Analysing the data</span>
                  <span /><span /><span />
                </div>
              </div>
            )}

            {error && (
              <div className="copilot-error">{error}</div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input row */}
          <div className="copilot-input-row">
            <textarea
              ref={inputRef}
              className="copilot-input"
              placeholder="Ask about financials…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              className="copilot-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || loading}
              aria-label="Send"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 8L2 2l3 6-3 6 12-6z" fill="white" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CopilotChat;
